/**
 * VoltageSelector.js
 *
 * Voltage selection dropdown with auto-match feature
 * Shows available voltages from frequency database
 * Auto mode finds closest available voltage to component's supply voltage
 *
 * Visual: Supply Voltage: [Auto-5V ▼] 🎯
 *
 * Only displays when:
 * - Component has frequency data
 * - Multiple voltages are available
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import useStore from '../store/useStore';
import { getClosestVoltage } from '../utils/freqDataInterpolator';
import THEME from '../styles/theme';

/**
 * Format voltage for display
 * Handles floating-point precision issues by rounding to 2 decimal places
 * and removing unnecessary trailing zeros
 */
const formatVoltage = (voltage) => {
  if (typeof voltage !== 'number') return voltage;
  // Round to 2 decimal places to handle floating-point precision
  const rounded = Math.round(voltage * 100) / 100;
  // Convert to string with up to 2 decimal places, removing trailing zeros
  return rounded.toFixed(2).replace(/\.?0+$/, '');
};

const VoltageSelector = ({ node }) => {
  const [availableVoltages, setAvailableVoltages] = useState([]);
  const getAvailableVoltages = useStore(state => state.getAvailableVoltages);
  const handleVoltageChange = useStore(state => state.handleVoltageChange);
  const showTutorial = useStore(state => state.showTutorial);
  const tutorialStep = useStore(state => state.tutorialStep);
  const completeTutorialAction = useStore(state => state.completeTutorialAction);

  const params = node.data?.parameters || {};
  const partNumber = node.data?.partNumber;
  const supplyVoltage = params.supplyVoltage?.value || 5.0;
  const voltageMode = params.supplyVoltageMode?.value || 'auto';
  // Round selectedVoltage to match the format of availableVoltages options
  const selectedVoltage = params.selectedVoltage?.value
    ? Math.round(params.selectedVoltage.value * 100) / 100
    : undefined;

  console.log('VoltageSelector render:', {
    partNumber,
    hasFreqData: params.hasFreqData?.value,
    nodeData: node.data
  });

  // Load available voltages - use pre-loaded values from node data if available
  useEffect(() => {
    // If voltages already loaded in node data during initialization, use those
    if (node.data.availableVoltages?.length > 0) {
      setAvailableVoltages(node.data.availableVoltages);
      return;
    }
    // Fallback: fetch if not pre-loaded (for existing nodes loaded from saved projects)
    if (partNumber && params.hasFreqData?.value) {
      getAvailableVoltages(partNumber)
        .then(voltages => {
          if (voltages?.length > 0) {
            setAvailableVoltages(voltages);
          }
        })
        .catch(error => {
          console.error('Failed to load available voltages:', error);
        });
    }
  }, [partNumber, params.hasFreqData?.value, node.data.availableVoltages, getAvailableVoltages]);

  // Don't show dropdown if no freq data
  if (!params.hasFreqData?.value) {
    // Return simple static display
    return (
      <VoltageRow>
        <VoltageLabel>Supply V:</VoltageLabel>
        <VoltageValue>{supplyVoltage.toFixed(1)} V</VoltageValue>
      </VoltageRow>
    );
  }

  // If loading voltages or only one voltage available, show simple display
  if (availableVoltages.length === 0) {
    // Still loading or no voltages found
    return (
      <VoltageRow>
        <VoltageLabel>Supply V:</VoltageLabel>
        <VoltageValue>{supplyVoltage.toFixed(1)} V (loading...)</VoltageValue>
      </VoltageRow>
    );
  }

  if (availableVoltages.length === 1) {
    // Only one voltage available, no need for dropdown
    return (
      <VoltageRow>
        <VoltageLabel>Supply V:</VoltageLabel>
        <VoltageValue>{formatVoltage(availableVoltages[0])} V</VoltageValue>
      </VoltageRow>
    );
  }

  // Calculate auto-matched voltage
  const autoMatchedVoltage = getClosestVoltage(supplyVoltage, availableVoltages);

  const handleChange = async (e) => {
    const value = e.target.value;
    console.log('VoltageSelector handleChange:', { value, nodeId: node.id, partNumber });
    try {
      if (value === 'auto') {
        console.log('Setting voltage to auto:', autoMatchedVoltage);
        await handleVoltageChange(node.id, autoMatchedVoltage, 'auto');
      } else {
        const voltage = parseFloat(value);
        console.log('Setting voltage to manual:', voltage);
        await handleVoltageChange(node.id, voltage, 'manual');
      }
      console.log('Voltage change completed successfully');
      // Track for tutorial (step 24 = data-sources/voltage change)
      if (showTutorial && tutorialStep === 24) {
        completeTutorialAction('voltageChanged');
      }
    } catch (error) {
      console.error('Failed to change voltage:', error);
    }
  };

  return (
    <VoltageRow>
      <VoltageLabel>Supply V:</VoltageLabel>
      <VoltageSelect
        className="nodrag"
        value={voltageMode === 'auto' ? 'auto' : selectedVoltage}
        onChange={handleChange}
        title="Select voltage for frequency-dependent parameters"
      >
        <option value="auto">Auto ({formatVoltage(autoMatchedVoltage)}V)</option>
        <optgroup label="──────────" />
        {availableVoltages.map(v => (
          <option key={v} value={v}>{formatVoltage(v)}V</option>
        ))}
      </VoltageSelect>
    </VoltageRow>
  );
};

// Styled Components
const VoltageRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const VoltageLabel = styled.div`
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.xs};
  flex-shrink: 0;
`;

const VoltageValue = styled.div`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.xs};
  font-weight: 600;
`;

const VoltageDropdownWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const VoltageSelect = styled.select`
  width: 75px;
  background: #3a3a3a;
  border: 1px solid #555;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: ${THEME.fontSize.xs};
  font-weight: 600;
  color: #e0e0e0;
  cursor: pointer;
  transition: all ${THEME.transitionFast};

  &:hover {
    border-color: #666;
  }

  &:focus {
    outline: none;
    border-color: #777;
    box-shadow: 0 0 0 1px #777;
  }

  option {
    background: #2a2a2a;
    color: #e0e0e0;
  }

  optgroup {
    background: #2a2a2a;
    color: #666;
    font-size: 10px;
  }
`;

const AutoBadge = styled.span`
  margin-left: 2px;
  font-size: 14px;
  cursor: help;
  animation: pulse 2s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.7;
      transform: scale(0.95);
    }
  }
`;

export default VoltageSelector;
